import {
  Add,
  FilterList as FilterListIcon,
  SearchOff,
  UnfoldLess,
  UnfoldMore,
} from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
  Badge,
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlowTestMeasurementFilterOptions } from "../../../hooks/FlowTest/useFlowTestMeasurements";
import { FlowTestMeasurementForm as FlowTestMeasurementFormType } from "../../../types/forms/flowTest";
import {
  FlowTestMeasurementGroupResponse,
} from "../../../types/model/flowTestMeasurement";
import { VariableResponse } from "../../../types/model/variable";
import { formatTimestamp } from "../../../utils";
import GenericFilter, { FilterField } from "../../General/GenericFilter";
import { MeasurementsImportDialog } from "../../Imports/FlowTestMeasurements/MeasurementsImportDialog";
import { GroupRow } from "./GroupRow";
import { NewGroupForm } from "./NewGroupForm";

export interface MeasurementInGroup {
  id: number;
  variableId: number;
  variableName: string;
  variableUnit?: string | null;
  value: number;
  flag?: string | null;
  notes?: string | null;
  measuredCaptureId: number;
  measuredAt?: string | null;
  testingTime?: number | null;
}

interface FlowTestMeasurementsProps {
  flowTestId: number;
  captureId?: number;
  entityId: number;
  measurementsData: FlowTestMeasurementGroupResponse[];
  canEdit: boolean;
  variables: VariableResponse[];
  filters: FlowTestMeasurementFilterOptions;
  onApplyFilters: (filters: FlowTestMeasurementFilterOptions) => void;
  onCreate: (values: FlowTestMeasurementFormType) => Promise<void>;
  onUpdate: (measurementId: number, values: FlowTestMeasurementFormType) => Promise<void>;
  onDelete: (measurementId: number) => Promise<void>;
  onRequestRefresh?: () => void;
  isChartMaximized: boolean;
  toggleMapMaximization: () => void;
  page: number;
  onPageChange: (page: number) => void;
}

function groupKey(g: FlowTestMeasurementGroupResponse) {
  return `${g.measuredCaptureId}_${g.measuredAt ?? g.testingTime}`;
}

export default function FlowTestMeasurements({
  flowTestId,
  captureId,
  entityId,
  measurementsData,
  canEdit,
  variables,
  filters,
  onApplyFilters,
  onCreate,
  onUpdate,
  onDelete,
  onRequestRefresh,
  isChartMaximized,
  toggleMapMaximization,
  page,
  onPageChange,
}: Readonly<FlowTestMeasurementsProps>) {
  const { t } = useTranslation();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openGroupKeys, setOpenGroupKeys] = useState<Set<string>>(
    () => new Set(measurementsData.map(groupKey))
  );
  const [creatingNew, setCreatingNew] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const allOpen = openGroupKeys.size === measurementsData.length;

  const toggleAll = () => {
    setOpenGroupKeys(allOpen ? new Set() : new Set(measurementsData.map(groupKey)));
  };

  const toggleGroup = (key: string) => {
    setOpenGroupKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleToggleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const isFilterOpen = Boolean(filterAnchorEl);

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        name: "variableId",
        label: t("flow-tests.flow-test-measurement.variable"),
        type: "autocomplete",
        options: variables.map((v) => ({ value: v.id, label: v.name })),
      },
    ];

    if (filters.xAxis === "measuredAt") {
      fields.push(
        { name: "startDate", label: t("flow-tests.flow-test-measurement.start-date"), type: "date" },
        { name: "endDate", label: t("flow-tests.flow-test-measurement.end-date"), type: "date" }
      );
    } else if (filters.xAxis === "testingTime") {
      fields.push(
        { name: "minTestingTime", label: t("flow-tests.flow-test-measurement.min-testing-time"), type: "number" },
        { name: "maxTestingTime", label: t("flow-tests.flow-test-measurement.max-testing-time"), type: "number" }
      );
    }

    fields.push({
      name: "xAxis",
      label: t("flow-tests.flow-test-measurement.order-by"),
      type: "toggle",
      options: [
        { value: "measuredAt", label: t("flow-tests.flow-test-measurement.measured-at") },
        { value: "testingTime", label: t("flow-tests.flow-test-measurement.testing-time") },
      ],
    });

    return fields;
  }, [variables, t, filters.xAxis]);

  const activeFilters = useMemo(() => {
    const list: { key: keyof FlowTestMeasurementFilterOptions; label: string; value: string }[] = [];
    if (filters.variableId) {
      const variable = variables.find((v) => v.id === filters.variableId);
      if (variable) {
        list.push({ key: "variableId", label: t("flow-tests.flow-test-measurement.variable"), value: variable.name });
      }
    }
    if (filters.startDate) {
      list.push({ key: "startDate", label: t("flow-tests.flow-test-measurement.start-date"), value: formatTimestamp(filters.startDate) });
    }
    if (filters.endDate) {
      list.push({ key: "endDate", label: t("flow-tests.flow-test-measurement.end-date"), value: formatTimestamp(filters.endDate) });
    }
    if (filters.minTestingTime !== undefined) {
      list.push({ key: "minTestingTime", label: t("flow-tests.flow-test-measurement.min-testing-time"), value: filters.minTestingTime.toString() });
    }
    if (filters.maxTestingTime !== undefined) {
      list.push({ key: "maxTestingTime", label: t("flow-tests.flow-test-measurement.max-testing-time"), value: filters.maxTestingTime.toString() });
    }
    return list;
  }, [filters, variables, t]);

  const handleClearFilters = () => {
    onApplyFilters({ xAxis: "measuredAt" });
  };

  const handleDeleteFilter = (key: keyof FlowTestMeasurementFilterOptions) => {
    onApplyFilters({ ...filters, [key]: undefined });
  };

  const visibleGroups = measurementsData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar disableGutters>
        <Typography sx={{ flex: "1 1 100%" }} variant="h5">
          {t("flow-tests.flow-test-measurement.title")}
        </Typography>

        <Tooltip title={isChartMaximized ? t("generic.minimize") : t("generic.maximize")}>
          <IconButton onClick={toggleMapMaximization}>
            <ShowChartIcon color={isChartMaximized ? "primary" : undefined} />
          </IconButton>
        </Tooltip>

        <Tooltip title={allOpen ? t("generic.collapse-all") : t("generic.expand-all")}>
          <IconButton onClick={toggleAll}>
            {allOpen ? <UnfoldLess /> : <UnfoldMore />}
          </IconButton>
        </Tooltip>

        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={handleToggleFilter}>
            <Badge badgeContent={activeFilters.length} color="primary">
              <FilterListIcon color={isFilterOpen ? "primary" : undefined} />
            </Badge>
          </IconButton>
        </Tooltip>

        {canEdit && (
          <>
            <Tooltip title={t("flow-tests.flow-test-measurement.add-new")}>
              <IconButton onClick={() => setCreatingNew((v) => !v)}>
                <Add />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("imports.import-dialog.title")}>
              <IconButton onClick={() => setImportOpen(true)}>
                <CloudUploadIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Toolbar>

      {activeFilters.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          {activeFilters.map((filter) => (
            <Chip
              key={filter.key}
              label={`${filter.label}: ${filter.value}`}
              onDelete={() => handleDeleteFilter(filter.key)}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      )}

      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { p: 3, width: 350 } } }}
      >
        <GenericFilter
          fields={filterFields}
          filters={filters}
          onFiltersChange={(newFilters) =>
            onApplyFilters(newFilters as FlowTestMeasurementFilterOptions)
          }
          onApplyFilters={(appliedFilters) =>
            onApplyFilters((appliedFilters as FlowTestMeasurementFilterOptions) ?? filters)
          }
          onClearFilters={handleClearFilters}
        />
      </Popover>

      {creatingNew && (
        <NewGroupForm
          flowTestId={flowTestId}
          captureId={captureId}
          entityId={entityId}
          onCreate={async (values) => {
            await onCreate(values);
            onRequestRefresh?.();
          }}
          onCancel={() => setCreatingNew(false)}
          variables={variables}
        />
      )}

      <Collapse in={!isChartMaximized} timeout="auto">
        <TableContainer>
          <Table sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>{t("flow-tests.flow-test-measurement.measured-at-or-testing-time")}</TableCell>
                <TableCell>{t("flow-tests.flow-test-measurement.measured-capture")}</TableCell>
                <TableCell>{t("flow-tests.flow-test-measurement.summary")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">{t("generic.no-records")}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                visibleGroups.map((group) => {
                  const key = groupKey(group);
                  return (
                    <GroupRow
                      key={key}
                      group={group}
                      variables={variables}
                      entityId={entityId}
                      open={openGroupKeys.has(key)}
                      canEdit={canEdit}
                      onToggle={() => toggleGroup(key)}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onCreate={onCreate}
                      flowTestId={flowTestId}
                      isEditing={true}
                      isSaving={false}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={measurementsData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            onPageChange(0);
          }}
        />
      </Collapse>

      <MeasurementsImportDialog
        flowTestId={flowTestId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => {
          setImportOpen(false);
          onRequestRefresh?.();
        }}
      />
    </Paper>
  );
}